import React from 'react';
import { Sparkles } from 'lucide-react';
import { AutocompleteSelect, type AutocompleteOption } from '../../../components/common/AutocompleteSelect';
import { useLanguage } from '../../../features/language/useLanguage';
import type { GbLotOption } from '../useSalesOrderForm';

interface SoLotPickerProps {
  index: number;
  disabled: boolean;
  selectedLotId: string;
  lots: GbLotOption[];
  onSelect: (lot: GbLotOption | null) => void;
}

// Sentinel id used by the AutocompleteSelect to represent the FIFO row. The
// form's lotId stays empty for FIFO; we only swap to this token to bridge
// AutocompleteSelect's id-based selection model.
const FIFO_SENTINEL_ID = '__fifo__';

/**
 * Compact lot picker for green-bean lines. Built on AutocompleteSelect so the user can
 * type-to-search lot numbers / process types. Empty form lotId == FIFO mode
 * (the backend resolves the actual lot at consumption time). A non-empty
 * lotId pins this line to a specific inventory_lots row.
 */
export const SoLotPicker: React.FC<SoLotPickerProps> = ({ index, disabled, selectedLotId, lots, onSelect }) => {
  const { t } = useLanguage();
  const fifoLot = lots[0];
  const selectedLot = selectedLotId ? lots.find((l) => l.id === selectedLotId) : null;
  const isFIFO = !selectedLotId;

  const fifoSummary = fifoLot
    ? `${fifoLot.lotNumber}${fifoLot.processType ? ` · ${fifoLot.processType}` : ''}`
    : t('salesOrder.items.lotPicker.noLots');

  const options: AutocompleteOption[] = React.useMemo(() => {
    const fifoOption: AutocompleteOption = {
      id: FIFO_SENTINEL_ID,
      label: `★ ${t('salesOrder.items.lotPicker.fifoAuto')} — ${fifoSummary}`,
      text: t('salesOrder.items.lotPicker.fifoAuto'),
    };
    const lotOptions: AutocompleteOption[] = lots.map((l) => ({
      id: l.id,
      label: `${l.lotNumber}${l.processType ? ` · ${l.processType}` : ''} · ${l.remaining.toFixed(2)} kg`,
      text: l.lotNumber,
    }));
    return [fifoOption, ...lotOptions];
  }, [lots, fifoSummary, t]);

  // The AC value tracks the option id, not the form lotId — we map between
  // the two boundaries here so the form's lotId stays empty for FIFO.
  const acValue = isFIFO ? FIFO_SENTINEL_ID : selectedLotId;

  // Trigger text — keeps a tiny FIFO pill in front when in auto mode so the
  // user can tell at a glance which lot will drain first.
  const displayText = isFIFO
    ? `${t('salesOrder.items.lotPicker.fifoAuto')} · ${fifoSummary}`
    : `${selectedLot?.lotNumber || t('salesOrder.items.lotPicker.unknownLot')}${
        selectedLot?.processType ? ` · ${selectedLot.processType}` : ''
      }`;

  return (
    <div className="w-full lg:w-[240px] lg:flex-none">
      <label className="block text-xs font-medium text-gray-500">{t('salesOrder.items.lotPicker.label')}</label>
      <div className="mt-2 relative">
        {isFIFO && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-2.5 top-1/2 z-10 inline-flex -translate-y-1/2 items-center gap-0.5 rounded-sm bg-primary-100 px-1 py-px text-[9px] font-bold uppercase tracking-wider text-primary-700"
          >
            <Sparkles className="h-2.5 w-2.5" aria-hidden="true" />FIFO
          </span>
        )}
        <AutocompleteSelect
          dataTestId={`so-item-lot-picker-${index}`}
          size="sm"
          allowCustom={false}
          disabled={disabled || lots.length === 0}
          value={acValue}
          displayValueOverride={displayText}
          inputClassName={isFIFO ? 'pl-[58px]' : undefined}
          options={options}
          onChange={(_, opt) => {
            if (!opt || opt.id === FIFO_SENTINEL_ID) {
              onSelect(null);
              return;
            }
            const lot = lots.find((l) => l.id === opt.id);
            if (lot) onSelect(lot);
          }}
          placeholder={t('salesOrder.items.lotPicker.label')}
        />
      </div>
    </div>
  );
};
